import { useState, type DragEvent } from "react";
import { Check } from "lucide-react";
import uploadIcon from "../assets/icons/ds/icon-upload.svg?raw";
import sidebarSimple from "../assets/icons/sidebar-simple.svg";
import { DsIcon } from "../components/DsIcon";
import { useShell } from "../components/ShellContext";
import { cx } from "../lib/cx";
import { Button } from "../components/ds";
import { dsControl } from "../components/ds/styles";
import type { OnboardingStep, OnboardingStepId } from "../lib/onboarding";
import type { Page } from "../routes";
import { settingsPageClass, settingsPageStyle } from "./settingsChrome";

const glass =
  "overflow-hidden rounded-ds-card bg-ds-glass font-ds text-ds-ink shadow-ds-card backdrop-blur-ds backdrop-saturate-[1.1]";

function copy(locale: string) {
  const tr = locale === "tr";
  return {
    welcome: tr ? "Hoş geldiniz" : "Welcome",
    lede: tr
      ? "İlk teklifinizden önce üç kısa adım. Toplamda yaklaşık 10 dakika."
      : "Three short steps before your first quote. About 10 minutes in total.",
    drop: tr ? "Çizimleri veya RFQ dosyalarını buraya bırakın" : "Drop drawings or RFQ files here",
    dropHint: tr
      ? "STEP, STP, DXF, DWG ve PDF. Montajın tamamını bir kerede yükleyin; malzeme listesi ve maliyet otomatik çıkarılır."
      : "STEP, STP, DXF, DWG and PDF. Upload whole assemblies at once; BOM and cost are extracted automatically.",
    sample: tr ? "Örnek dosyayı dene" : "Try a sample file",
    choose: tr ? "Dosya seç" : "Choose files",
    setup: tr ? "Atölyenizi kurun" : "Set up your workshop",
    progress: (done: number) => (tr ? `${done} / 4 tamam` : `${done} of 4 done`),
    done: tr ? "Tamam" : "Done",
    account: tr ? "Hesap ve atölye adı" : "Account and workshop name",
    accountHint: tr ? "Adınızı ve atölye adını kaydedin." : "Save your name and the workshop name.",
    accountAction: tr ? "Hesabı aç" : "Open account",
    machines: tr ? "Makinelerinizi ve saatlik maliyetleri ekleyin" : "Add your machines and hourly costs",
    machinesHint: tr
      ? "Makine yokken işleme ve işçilik 0 ₺ maliyetlenir."
      : "Without machines, processing and labour are costed at 0 ₺",
    addMachine: tr ? "Makine ekle" : "Add machine",
    stock: tr ? "Stok listenizi içe aktarın" : "Import your stock list",
    stockHint: tr
      ? "Excel veya CSV. BOM satırları bu listeyle eşleştirilir."
      : "Excel or CSV. BOM lines are matched against this list.",
    import: tr ? "İçe aktar" : "Import",
    drawing: tr ? "İlk çiziminizi yükleyin" : "Upload your first drawing",
    drawingHint: tr ? "Ya da sistemi örnek bir montajla deneyin." : "Or try the system with a sample assembly.",
    nothing: tr ? "Henüz bekleyen bir şey yok" : "Nothing waiting yet",
    nothingHint: tr
      ? "Çizimleri yükledikçe kararınızı bekleyen adımlar önceliğe göre burada görünür."
      : "As you upload drawings, steps that need your decision show up here by priority.",
  };
}

const STEP_NO: Record<OnboardingStepId, number> = {
  account: 1,
  machines: 2,
  stock: 3,
  drawing: 4,
};

interface Props {
  locale: string;
  steps: OnboardingStep[];
  onNavigate: (page: Page, id?: string | null, sub?: string | null) => void;
  onChooseFiles: () => void;
  onSample: () => void;
  onFiles: (files: File[]) => void;
  uploads?: { id: string; name: string; state: string; message: string }[];
}

export function HomeFirstRun({ locale, steps, onNavigate, onChooseFiles, onSample, onFiles, uploads = [] }: Props) {
  const shell = useShell();
  const text = copy(locale);
  const done = steps.filter((step) => step.done).length;
  const byId = new Map(steps.map((step) => [step.id, step.done]));
  const [drag, setDrag] = useState(false);

  function takeDrop(event: DragEvent) {
    event.preventDefault();
    setDrag(false);
    const files = event.dataTransfer?.files ? [...event.dataTransfer.files] : [];
    if (files.length > 0) onFiles(files);
  }

  return (
    <div
      data-theme="light"
      className={settingsPageClass}
      style={settingsPageStyle}
      data-testid="home"
      data-mode="first-run"
    >
      <header className="flex items-start gap-2">
        {shell.narrow ? (
          <button
            type="button"
            className={cx(dsControl, "inline-flex size-[38px] shrink-0 items-center justify-center rounded-full bg-ds-glass p-0")}
            aria-label={shell.sidebarOpen ? "Close menu" : "Open menu"}
            data-testid="menu"
            onClick={shell.toggleSidebar}
          >
            <img src={sidebarSimple} alt="" className="size-5" />
          </button>
        ) : null}
        <div className="min-w-0">
          <h1 className="m-0 font-ds text-ds-page font-semibold text-ds-ink" data-testid="page-heading">
            {text.welcome}
          </h1>
          <p className="m-0 mt-0.5 text-ds-body font-normal text-ds-ink-3">{text.lede}</p>
        </div>
      </header>

      <div
        className={cx(
          "flex min-w-0 flex-wrap items-center gap-4 rounded-ds-card border border-dashed bg-ds-glass px-4 py-3.5",
          drag ? "border-ds-amber bg-ds-amber-soft/40" : "border-ds-hair",
        )}
        data-testid="home-dropzone"
        onDragEnter={(event) => {
          event.preventDefault();
          setDrag(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={takeDrop}
      >
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-ds-glass-strong">
          <DsIcon svg={uploadIcon} className="size-4 text-ds-ink" />
        </span>
        <span className="min-w-[200px] flex-1">
          <span className="block text-ds-item font-semibold text-ds-ink">{text.drop}</span>
          <span className="mt-0.5 block text-ds-body font-normal text-ds-ink-3">{text.dropHint}</span>
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="secondary" data-testid="home-sample" onClick={onSample}>
            {text.sample}
          </Button>
          <Button variant="primary" data-testid="home-choose-files" onClick={onChooseFiles}>
            {text.choose}
          </Button>
        </span>
      </div>

      {uploads.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid="home-uploads">
          {uploads.map((row) => (
            <li key={row.id} className="text-ds-body text-ds-ink" data-state={row.state}>
              <span className="font-medium">{row.name}</span>
              <span className="text-ds-ink-3"> — {row.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid items-stretch gap-4 min-[901px]:grid-cols-[minmax(0,1.45fr)_minmax(260px,0.8fr)]">
        <section className={cx(glass, "min-w-0")} data-testid="home-onboarding" aria-labelledby="home-onboarding-h">
          <div className="flex items-center gap-3 px-[18px] pt-3.5">
            <h2 id="home-onboarding-h" className="m-0 flex-1 font-ds text-ds-item font-semibold text-ds-ink">
              {text.setup}
            </h2>
            <span className="text-ds-body text-ds-ink-3" data-testid="home-onboarding-progress">
              {text.progress(done)}
            </span>
          </div>
          <div className="px-[18px] pt-2.5 pb-1" aria-hidden="true">
            <div className="h-1.5 overflow-hidden rounded-full bg-ds-hover">
              <div className="h-full rounded-full bg-ds-amber" style={{ width: `${(done / 4) * 100}%` }} />
            </div>
          </div>
          <ol className="m-0 flex list-none flex-col p-0 pb-1.5">
            <Step
              id="account"
              done={byId.get("account") === true}
              title={text.account}
              detail={byId.get("account") ? text.done : text.accountHint}
              action={byId.get("account") ? undefined : text.accountAction}
              onAction={() => onNavigate("profile")}
            />
            <Step
              id="machines"
              done={byId.get("machines") === true}
              title={text.machines}
              detail={byId.get("machines") ? text.done : text.machinesHint}
              detailTestId={byId.get("machines") ? undefined : "home-labour-note"}
              action={byId.get("machines") ? undefined : text.addMachine}
              actionTestId="home-add-machine"
              primary
              onAction={() => onNavigate("profile", "machines")}
            />
            <Step
              id="stock"
              done={byId.get("stock") === true}
              title={text.stock}
              detail={byId.get("stock") ? text.done : text.stockHint}
              action={byId.get("stock") ? undefined : text.import}
              actionTestId="home-import"
              onAction={() => onNavigate("erp")}
            />
            <Step
              id="drawing"
              done={byId.get("drawing") === true}
              title={text.drawing}
              detail={byId.get("drawing") ? text.done : text.drawingHint}
              action={byId.get("drawing") ? undefined : text.sample}
              actionTestId="home-onboarding-sample"
              onAction={onSample}
            />
          </ol>
        </section>

        <section
          className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-ds-card border border-dashed border-ds-hair bg-ds-glass px-8 py-10 text-center"
          data-testid="home-waiting"
          aria-label={text.nothing}
        >
          <h2 className="m-0 font-ds text-ds-item font-semibold text-ds-ink">{text.nothing}</h2>
          <p className="m-0 mt-2 max-w-[280px] text-ds-body leading-5 text-ds-ink-3" data-testid="home-waiting-empty">
            {text.nothingHint}
          </p>
        </section>
      </div>
    </div>
  );
}

function Step({
  id,
  done,
  title,
  detail,
  detailTestId,
  action,
  actionTestId,
  primary,
  onAction,
}: {
  id: OnboardingStepId;
  done: boolean;
  title: string;
  detail: string;
  detailTestId?: string;
  action?: string;
  actionTestId?: string;
  primary?: boolean;
  onAction: () => void;
}) {
  return (
    <li
      className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-3 border-t border-ds-hair px-[18px] py-3 first:border-t-0"
      data-testid={`home-onboarding-${id}`}
      data-state={done ? "done" : "todo"}
    >
      {done ? (
        <span className="inline-flex size-6 items-center justify-center rounded-full bg-ds-amber text-ds-on-amber" aria-hidden="true">
          <Check size={14} strokeWidth={2.5} />
        </span>
      ) : (
        <span
          className="inline-flex size-6 items-center justify-center rounded-full bg-ds-glass-strong font-ds text-ds-badge font-medium text-ds-ink-3"
          aria-hidden="true"
        >
          {STEP_NO[id]}
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-ds-body font-semibold text-ds-ink">{title}</span>
        <span className="mt-0.5 block text-ds-badge leading-4 text-ds-ink-3" data-testid={detailTestId}>
          {detail}
        </span>
      </span>
      {action ? (
        <Button variant={primary ? "primary" : "secondary"} data-testid={actionTestId} onClick={onAction}>
          {action}
        </Button>
      ) : null}
    </li>
  );
}
